/**
 * Provides the Titles table with the shared two-row action-bar experience while
 * preserving the filter and search behavior that previously lived in TitlesFilterBar.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from '@amzn/meridian/button';
import Select, { SelectOption } from '@amzn/meridian/select';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import { COUNTRY_OPTIONS } from 'src/components/common/CampaignFilterBar';
import { DataTableActionBar } from 'src/components/common/data-table-action-bar/DataTableActionBar';
import { useAssetsControllerGetDistinctCampaignOptionsQuery } from 'src/store/leela-api';
import type { TitlesFilterState } from 'src/types/titles.types';
import { excludeArchivedCampaignOptions } from 'src/utils/campaignOptions';
interface CampaignOption {
  readonly id: string;
  readonly name: string;
  readonly productStrategies?: unknown[];
  readonly publisher: string;
  readonly status?: string;
}
interface TitlesFilterPopoverProps {
  campaignOptions: Array<{ id: string; name: string }>;
  campaignSearchQuery: string;
  filters: TitlesFilterState;
  localeOptions: string[];
  publisherOptions: string[];
  onCampaignSearch: (query: string) => void;
  onCountryChange: (country: string) => void;
  onFiltersChange: (filters: TitlesFilterState) => void;
}
interface TitlesFilterContentProps {
  campaignOptions: Array<{ id: string; name: string }>;
  filters: TitlesFilterState;
  localSearch: string;
  onClearAll: () => void;
  onFiltersChange: (filters: TitlesFilterState) => void;
  onSearchClear: () => void;
}
export interface TitlesActionBarProps {
  filters: TitlesFilterState;
  onClearAll?: () => void;
  onFiltersChange: (filters: TitlesFilterState) => void;
  search: string;
  onSearchChange: (search: string) => void;
  campaignOptions?: CampaignOption[];
}
const DEBOUNCE_MS = 300;
const deriveLocales = (
  campaigns: CampaignOption[] | undefined,
  country: string,
): string[] => {
  if (!campaigns || !country) return [];
  const locales = new Set<string>();
  campaigns.forEach((campaign) => {
    const strategies = campaign.productStrategies ?? [];
    (strategies as Array<{ country?: string; locales?: string[] }>).forEach((strategy) => {
      if (strategy.country === country) strategy.locales?.forEach((locale) => locales.add(locale));
    });
  });
  return Array.from(locales).sort();
};
const TitlesFilterPopover: React.FC<TitlesFilterPopoverProps> = ({
  campaignOptions,
  campaignSearchQuery,
  filters,
  localeOptions,
  publisherOptions,
  onCampaignSearch,
  onCountryChange,
  onFiltersChange,
}) => {
  const { t } = useTranslation();
  return (
    <>
      <Select
        data-testid="titles-country-filter"
        label={t('country')}
        placeholder={t('select_country')}
        size="small"
        value={filters.country}
        width={200}
        onChange={onCountryChange}
      >
        <SelectOption key="__all__" label={t('all')} value="" />
        {COUNTRY_OPTIONS.map((country) => (
          <SelectOption key={country} label={country} value={country} />
        ))}
      </Select>
      <Select
        data-testid="titles-locale-filter"
        disabled={!filters.country}
        label={t('form_locale')}
        placeholder={t('select_locale')}
        size="small"
        value={filters.locale}
        width={200}
        onChange={(locale: string) => { onFiltersChange({ ...filters, locale }); }}
      >
        <SelectOption key="__all__" label={t('all')} value="" />
        {localeOptions.map((locale) => (
          <SelectOption key={locale} label={locale} value={locale} />
        ))}
      </Select>
      <Select
        data-testid="titles-publisher-filter"
        label={t('publisher')}
        placeholder={t('all')}
        size="small"
        value={filters.publisher}
        width={200}
        onChange={(publisher: string) => { onFiltersChange({ ...filters, publisher }); }}
      >
        <SelectOption key="__all__" label={t('all')} value="" />
        {publisherOptions.map((publisher) => (
          <SelectOption key={publisher} label={publisher} value={publisher} />
        ))}
      </Select>
      <Select
        data-testid="titles-campaign-filter"
        label={t('campaign')}
        placeholder={t('search_campaigns')}
        popoverMaxHeight={300}
        searchQuery={campaignSearchQuery}
        size="small"
        value={filters.campaignIds}
        width={200}
        onChange={(campaignIds: string[]) => { onFiltersChange({ ...filters, campaignIds }); }}
        onSearch={onCampaignSearch}
      >
        {campaignOptions.length === 0
          ? <SelectOption disabled label={t('table_no_results')} value="" />
          : campaignOptions.map((campaign) => (
            <SelectOption key={campaign.id} label={campaign.name} value={campaign.id} />
          ))}
      </Select>
    </>
  );
};
const TitlesFilterContent: React.FC<TitlesFilterContentProps> = ({
  campaignOptions,
  filters,
  localSearch,
  onClearAll,
  onFiltersChange,
  onSearchClear,
}) => {
  const { t } = useTranslation();
  const getCampaignName = (id: string): string => (
    campaignOptions.find((campaign) => campaign.id === id)?.name ?? id
  );
  const hasActiveFilters = Boolean(
    filters.country || filters.locale || filters.publisher || filters.campaignIds.length || localSearch,
  );
  if (!hasActiveFilters) return <Text color="secondary" type="b300">{t('no_filters_applied')}</Text>;
  return (
    <>
      {filters.country && (
        <Tag data-testid="filter-tag-country" type="theme" onClose={() => { onFiltersChange({ ...filters, country: '', locale: '' }); }}>
          {`${t('country')}: ${filters.country}`}
        </Tag>
      )}
      {filters.locale && (
        <Tag data-testid="filter-tag-locale" type="theme" onClose={() => { onFiltersChange({ ...filters, locale: '' }); }}>
          {`${t('form_locale')}: ${filters.locale}`}
        </Tag>
      )}
      {filters.publisher && (
        <Tag data-testid="filter-tag-publisher" type="theme" onClose={() => { onFiltersChange({ ...filters, publisher: '' }); }}>
          {`${t('publisher')}: ${filters.publisher}`}
        </Tag>
      )}
      {filters.campaignIds.map((id) => (
        <Tag
          data-testid={`filter-tag-campaign-${id}`}
          key={id}
          type="theme"
          onClose={() => { onFiltersChange({ ...filters, campaignIds: filters.campaignIds.filter((campaignId) => campaignId !== id) }); }}
        >
          {`${t('campaign')}: ${getCampaignName(id)}`}
        </Tag>
      ))}
      {localSearch && (
        <Tag data-testid="filter-tag-search" type="theme" onClose={onSearchClear}>
          {`${t('search')}: "${localSearch}"`}
        </Tag>
      )}
      <Button data-testid="titles-clear-all-filters" type="link" onClick={onClearAll}>
        {t('clear_filters')}
      </Button>
    </>
  );
};
export const TitlesActionBar: React.FC<TitlesActionBarProps> = ({
  filters,
  onClearAll,
  onFiltersChange,
  search,
  onSearchChange,
  campaignOptions: campaignOptionsProp,
}) => {
  const { t } = useTranslation();
  const [campaignSearchQuery, setCampaignSearchQuery] = useState('');
  const [localSearch, setLocalSearch] = useState(search);
  const onSearchChangeRef = useRef(onSearchChange);
  useEffect(() => { onSearchChangeRef.current = onSearchChange; });
  useEffect(() => { setLocalSearch(search); }, [search]);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearch !== search) onSearchChangeRef.current(localSearch);
    }, DEBOUNCE_MS);
    return () => { clearTimeout(timer); };
  }, [localSearch, search]);
  const { data: fetchedCampaignOptions } = useAssetsControllerGetDistinctCampaignOptionsQuery(
    undefined,
    { skip: Boolean(campaignOptionsProp) },
  );
  const allCampaignOptions = campaignOptionsProp ?? fetchedCampaignOptions;
  const localeOptions = useMemo(
    () => deriveLocales(allCampaignOptions, filters.country),
    [allCampaignOptions, filters.country],
  );
  const publisherOptions = useMemo(() => Array.from(new Set(
    (allCampaignOptions ?? []).map((campaign) => campaign.publisher).filter(Boolean),
  )).sort(), [allCampaignOptions]);
  const campaignSelectOptions = useMemo(
    () => excludeArchivedCampaignOptions(allCampaignOptions ?? [])
      .map((campaign) => ({ id: campaign.id, name: campaign.name })),
    [allCampaignOptions],
  );
  const filteredCampaignOptions = useMemo(() => {
    const query = campaignSearchQuery.toLowerCase();
    return campaignSelectOptions.filter((campaign) => (
      !query || campaign.name.toLowerCase().includes(query) || campaign.id.toLowerCase().includes(query)
    ));
  }, [campaignSearchQuery, campaignSelectOptions]);
  const handleCountryChange = useCallback((country: string) => {
    const locale = country ? deriveLocales(allCampaignOptions, country)[0] ?? '' : '';
    onFiltersChange({ ...filters, country, locale });
  }, [allCampaignOptions, filters, onFiltersChange]);
  const handleSearchClear = useCallback(() => {
    setLocalSearch('');
    onSearchChangeRef.current('');
  }, []);
  const handleClearAll = useCallback(() => {
    setCampaignSearchQuery('');
    setLocalSearch('');
    if (onClearAll) {
      onClearAll();
      return;
    }
    onFiltersChange({ ...filters, country: '', locale: '', publisher: '', campaignIds: [] });
    onSearchChangeRef.current('');
  }, [filters, onClearAll, onFiltersChange]);
  return (
    <DataTableActionBar
      filterButtonLabel={t('filter_titles_header')}
      filterButtonTestId="titles-filter-button"
      filterContent={(
        <TitlesFilterContent
          campaignOptions={campaignSelectOptions}
          filters={filters}
          localSearch={localSearch}
          onClearAll={handleClearAll}
          onFiltersChange={onFiltersChange}
          onSearchClear={handleSearchClear}
        />
      )}
      filterPopoverContent={(
        <TitlesFilterPopover
          campaignOptions={filteredCampaignOptions}
          campaignSearchQuery={campaignSearchQuery}
          filters={filters}
          localeOptions={localeOptions}
          publisherOptions={publisherOptions}
          onCampaignSearch={setCampaignSearchQuery}
          onCountryChange={handleCountryChange}
          onFiltersChange={onFiltersChange}
        />
      )}
      search={{
        value: localSearch,
        placeholder: t('search_titles'),
        testId: 'titles-search-input',
        width: 350,
        onChange: setLocalSearch,
        onSubmit: () => { onSearchChangeRef.current(localSearch); },
      }}
      onClearFilters={handleClearAll}
    />
  );
};
